// ─────────────────────────────────────────────────────────────────────────────
// Crew — client-side funnel continuity (Agent B).
//
// The Worker API exposes NO list endpoints for goals/polls/intents (by design for
// this slice) — only create/patch. So the app holds the freshly-created entities
// in React state. To survive a page reload mid-flow (and keep deep-links useful)
// we mirror the active goal/poll/intent objects into sessionStorage, keyed to the
// group id. This is purely a UX convenience: the server stays the source of truth
// for every mutation, and nothing here is ever trusted for a gated write.
// ─────────────────────────────────────────────────────────────────────────────

import type { BookingIntent, Goal, TimePoll } from './types';

export interface FunnelState {
  goal: Goal | null;
  poll: TimePoll | null;
  intent: BookingIntent | null;
}

const EMPTY: FunnelState = { goal: null, poll: null, intent: null };

function key(groupId: string): string {
  return `crew.funnel.${groupId}`;
}

export function loadFunnelState(groupId: string): FunnelState {
  try {
    const raw = sessionStorage.getItem(key(groupId));
    if (!raw) return { ...EMPTY };
    const parsed = JSON.parse(raw) as Partial<FunnelState>;
    return {
      goal: parsed.goal ?? null,
      poll: parsed.poll ?? null,
      intent: parsed.intent ?? null,
    };
  } catch {
    return { ...EMPTY };
  }
}

export function saveFunnelState(groupId: string, state: FunnelState): void {
  try {
    sessionStorage.setItem(key(groupId), JSON.stringify(state));
  } catch {
    /* storage unavailable — in-memory React state still drives the UI */
  }
}

export function clearFunnelState(groupId: string): void {
  try {
    sessionStorage.removeItem(key(groupId));
  } catch {
    /* no-op */
  }
}
